import {
  Project,
  ProjectVersion,
  RenderConfig,
  RENDER_CONFIG_VERSION,
} from "types";

/**
 * The standards a declaration of conformity would list: the project's own
 * selection plus every standard applied by a risk mitigation, de-duplicated.
 *
 * This mirrors the union built in certain-pdf's Conformity.tsx, including its
 * "NAME:VERSION" shape filter, so the checkboxes offered here match what the
 * document actually renders.
 */
export const conformityStandards = (project?: Project): string[] => {
  if (!project) {
    return [];
  }

  const mitigationStandards =
    project.risks?.flatMap((risk) =>
      risk.mitigations?.flatMap((mitigation) => mitigation.applied_standards)
    ) || [];

  return Array.from(
    new Set<string>([...mitigationStandards, ...(project.standards || [])])
  )
    .filter((standard) => !!standard)
    .filter((standard) => /^[^:]+:.+$/.test(standard))
    .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
};

/**
 * The most recent render, whether it was a dry run or an official revision.
 *
 * Selected by timestamp rather than array position on purpose: getLatestVersionDocs
 * in pages/Documents/Documents.tsx sorts `project.versions` in place, which
 * reorders the array held in the React Query cache. Anything reading by index
 * here would get whichever end that sort happened to leave behind.
 */
export const latestVersion = (
  project?: Project
): ProjectVersion | undefined => {
  if (!project?.versions?.length) {
    return undefined;
  }

  return project.versions.reduce((latest, candidate) => {
    const latestAt = new Date(latest.createdAt).getTime();
    const candidateAt = new Date(candidate.createdAt).getTime();

    if (Number.isNaN(candidateAt)) {
      return latest;
    }

    return Number.isNaN(latestAt) || candidateAt >= latestAt
      ? candidate
      : latest;
  });
};

/** The config the previous render used, so the dialog can offer "same again". */
export const previousRenderConfig = (
  project?: Project
): RenderConfig | undefined => latestVersion(project)?.renderConfig;

const sameMembers = (a: string[], b: string[]) =>
  a.length === b.length && a.every((item) => b.includes(item));

/**
 * Whether this selection would render differently from the previous one. Used
 * to tell the user they are about to change a signed document rather than
 * reproduce it.
 */
export const differsFromPrevious = (
  previous: RenderConfig | undefined,
  showManufacturingYear: boolean,
  excludedStandards: string[]
): boolean => {
  const previousYear = previous?.showManufacturingYear ?? true;
  const previousExcluded = previous?.excludedStandards || [];

  return (
    previousYear !== showManufacturingYear ||
    !sameMembers(previousExcluded, excludedStandards)
  );
};

/**
 * Builds the config to send. Returns undefined when nothing deviates from the
 * defaults so the field can be omitted from the request entirely.
 */
export const buildRenderConfig = (
  showManufacturingYear: boolean,
  excludedStandards: string[]
): RenderConfig | undefined => {
  if (showManufacturingYear && excludedStandards.length === 0) {
    return undefined;
  }

  return {
    version: RENDER_CONFIG_VERSION,
    showManufacturingYear,
    excludedStandards,
  };
};
